import { Button } from 'src/components/Button/Button.component';
import Loader from 'src/components/Loader/Loader.component';
import Translate from 'src/components/Translate/Translate.component';
import { ResetPayByLinkStore } from 'src/pages/createLinkFlow/CreateLinkFlow.config';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RoutePath } from 'src/routers/routers.config';
import { initOrderCreateMethodApi } from 'src/services/orderLink/orderLink.request';
import { usePayByLinkStore } from 'src/store/store';
import { H4, PSmall } from 'src/style-utils/typographic';
import { CategoryCodes, ProductCodes } from 'src/utils/types/common.types';
import type { Prospect } from './ProductCard.types';
import ProductCardCSTModalComponent from './modal/ProductCardCSTModal.component';
import Icon from '../Icon/Icon.component';
import { Flex } from '../../style/flexbox.style';
import { StyleSheetManager } from 'styled-components';
import { shouldForwardProp } from 'src/style-utils/functions';

type CustomDetails = {
  InstallmentsCount: number;
  Name: string;
  Prospect: Prospect[];
};

type Props = {
  categoryCode: CategoryCodes;
  visual: string;
  productCode: ProductCodes;
  customDetail: CustomDetails;
  disabled: boolean;
  fromMonitoring?: string;
};

const ProductCardCST = ({
  productCode,
  categoryCode,
  visual,
  customDetail,
  disabled,
  fromMonitoring,
}: Props) => {
  const setValue = usePayByLinkStore((state) => state.setValue);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const setValues = usePayByLinkStore((state) => state.setValues);
  const title = customDetail.Name;
  const prospect = customDetail.Prospect;
  const { mutate: mutateInit, isPending: isLoadingInit } = useMutation({
    mutationFn: initOrderCreateMethodApi,
    onSuccess: (res) => {
      setValue('stateFlow', true);
      setValue('uwId', res?.data?.ResultSet.UwId);
      setValue('sessionGuid', res?.data?.ResultSet.Guid);
      setValue('inviteEmail', res?.data?.ResultSet.LastInviteEmail);
      navigate(
        `${RoutePath.createLink}${categoryCode}/${productCode}/${RoutePath.stepImport}`,
      );
    },
    onError: () => {},
  });
  const SvgIcon = visual
    ? () => <div dangerouslySetInnerHTML={{ __html: visual }} />
    : null;
  if (isLoadingInit) {
    return <Loader overlayViewMode="fullscreen" active viewMode="fluid" />;
  }
  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <div className="flex min-h-[56rem] w-70 flex-col overflow-hidden rounded-xl bg-white shadow-blue4px lg:min-h-[60rem] lg:w-96 lg:shadow-blue8px">
        <div className="flex h-40 items-center bg-pblack-400 px-6 py-8">
          <H4 textColor="white">{title}</H4>
        </div>
        <div className="flex flex-1 flex-col gap-4 px-4 py-6 lg:gap-8 lg:px-6 lg:py-8">
          {SvgIcon ? (
            <Flex align="center" justify="center">
              <Icon
                svgIconComponent={SvgIcon}
                iconHeight="17.6rem"
                iconWidth="19.8rem"
                marginBottom="1.2rem"
                className="h-36 w-auto lg:h-44"
              />
            </Flex>
          ) : null}
          <div className="flex flex-1 flex-col gap-3">
            <PSmall colorBlack>
              <Translate id="lbl.totalInstallment" />
              :&nbsp;
              {customDetail.InstallmentsCount}
            </PSmall>
            <PSmall colorBlack>
              <Translate id="text.rataPlanning" />
              :&nbsp;
              <Button
                underline
                variant="LinkPrimary"
                padding="0"
                margin="0"
                height="max-content"
                fontWeight="400"
                onClick={() => setShowModal(true)}
              >
                <PSmall colorBlack>
                  <Translate id="lbl.seeDetail" />
                </PSmall>
              </Button>
            </PSmall>
          </div>
          <Button
            variant="Tertiary"
            minWidth="100%"
            sizeOnDesktop="medium"
            disabled={disabled}
            onClick={() => {
              setValues(ResetPayByLinkStore);
              if (fromMonitoring) {
                mutateInit({ BuyerGuid: fromMonitoring });
                return;
              }
              navigate(
                `${RoutePath.createLink}${categoryCode}/${productCode}/${RoutePath.stepCustomer}`,
              );
            }}
          >
            <Translate id="text.createLink" />
          </Button>
        </div>
      </div>
      {showModal && (
        <ProductCardCSTModalComponent
          buttonTitle="text.okUnderstand"
          modalTitle="text.rataPlanning"
          prospect={prospect}
          handleClose={() => setShowModal(false)}
          i18n
        />
      )}
    </StyleSheetManager>
  );
};

export default ProductCardCST;
